import React from 'react';
import { useAuth } from '../store/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PostLogin } from '../services/admin';
import {
  getFirstAllowedRoute,
  normalizeDepartment,
  normalizeRole,
  resolvePermissions,
} from '../utils/adminPermissions';

interface WithUserAuthProps {
  onLogin?: (userName: string, userPassword: string) => Promise<any>;
  onLogout?: () => void;
}

const withUserAuth = <P extends object>(
  WrappedComponent: React.ComponentType<P & WithUserAuthProps>,
) => {
  return (props: P) => {
    const { login, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogin = async (userName: string, userPassword: string) => {
      const response = await PostLogin({
        email: userName,
        password: userPassword,
      });

      if (response?.assessToken) {
        const role = normalizeRole(response.role, response.type);
        const department = normalizeDepartment(response.department);
        const permissions = resolvePermissions(
          role,
          department,
          response.permissions || null,
        );
        const result = login({
          token: response.assessToken,
          userName: response.name || userName,
          userId: userName,
          role,
          department,
          permissions,
          useCustomPermissions: Boolean(
            response.useCustomPermissions || response.permissions?.useCustomPermissions,
          ),
          userType: String(response.type ?? role),
        });

        if (result) {
          return {
            status: true,
            message: 'Logged In Successfully',
            landingPath: getFirstAllowedRoute(permissions),
          };
        }
        throw Error('Loggin Failed');
      }

      throw Error('Login Failed');
    };

    const handleLogOut = () => {
      logout();
      setTimeout(() => {
        navigate('/');
      }, 1000);
    };

    return (
      <WrappedComponent
        {...(props as P)}
        onLogin={handleLogin}
        onLogout={handleLogOut}
      />
    );
  };
};

export default withUserAuth;
